import React, { useState } from 'react';
import { Heart, User, Calendar as CalendarIcon, Ruler, Weight, Droplet, Baby } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BabySetup = ({ onBabyCreated }) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    date_of_birth: '',
    gender: ''
  });
  const [loading, setLoading] = useState(false);
  const [profileCreationEnabled, setProfileCreationEnabled] = useState(true);
  const [checkingSettings, setCheckingSettings] = useState(true);

  // Check if profile creation is enabled
  React.useEffect(() => {
    const checkProfileCreationStatus = async () => {
      try {
        const response = await axios.get(`${API}/baby-app-settings`);
        setProfileCreationEnabled(response.data.profile_creation_enabled);
      } catch (error) {
        console.error('Error checking profile creation status:', error);
        // Default to enabled if error
        setProfileCreationEnabled(true);
      } finally {
        setCheckingSettings(false);
      }
    };
    checkProfileCreationStatus();
  }, []);

  const handleInputChange = (field, value) => {
    // Auto-capitalize first letter of each word for name fields
    if (field === 'name') {
      value = value.split(' ').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).join(' ');
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle date input with auto-advance
  const handleDateInput = (e) => {
    let value = e.target.value;
    
    // Remove all non-numeric characters except /
    value = value.replace(/[^\d/]/g, '');
    
    // Auto-add slashes and format
    if (value.length === 2 && !value.includes('/')) {
      value = value + '/';
    } else if (value.length === 5 && value.split('/').length === 2) {
      value = value + '/';
    }
    
    // Limit to dd/mm/yyyy format (10 characters)
    if (value.length > 10) {
      value = value.substring(0, 10);
    }
    
    setFormData(prev => ({ ...prev, date_of_birth: value }));
  };

  // Handle backspace for natural deletion
  const handleDateKeyDown = (e) => {
    if (e.key === 'Backspace') {
      const input = e.target;
      const value = input.value;
      const cursorPosition = input.selectionStart;
      
      // If cursor is right after a slash, delete the slash and the character before it
      if (cursorPosition > 0 && value[cursorPosition - 1] === '/') {
        e.preventDefault();
        const newValue = value.substring(0, cursorPosition - 2) + value.substring(cursorPosition);
        setFormData(prev => ({ ...prev, date_of_birth: newValue }));
        // Set cursor position after the state updates
        setTimeout(() => {
          input.selectionStart = input.selectionEnd = cursorPosition - 2;
        }, 0);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.date_of_birth || !formData.gender) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      // Check for duplicate baby
      const duplicateCheck = await axios.post(`${API}/babies/check-duplicate`, {
        name: formData.name,
        date_of_birth: formData.date_of_birth
      });

      if (duplicateCheck.data.exists) {
        const existingBaby = duplicateCheck.data.baby;
        toast.error(`Already exists: ${existingBaby.name} (${existingBaby.date_of_birth})`);
        setLoading(false);
        return;
      }

      const babyData = {
        name: formData.name,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        // Set default values for required backend fields
        height: 50.0, // Default height in cm
        weight: 3.0   // Default weight in kg
      };
      
      await onBabyCreated(babyData);
      toast.success('Profile created successfully!');
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error('Failed to create profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking settings
  if (checkingSettings) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show message if profile creation is disabled
  if (!profileCreationEnabled) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-white to-green-50">
        <div className="w-full max-w-md">
          <div className="form-container">
            <div className="form-header">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
                <Baby className="w-8 h-8 text-white" />
              </div>
              <h1>Welcome to Health Record</h1>
            </div>
            <div className="text-center py-8">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 mb-4">
                <p className="text-orange-800 font-medium mb-2">Profile Creation Currently Disabled</p>
                <p className="text-sm text-orange-600">
                  Creating new baby profiles is currently disabled by the administrator. Please contact support for assistance.
                </p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="mt-4"
              >
                Go to Login
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-blue-50 via-white to-green-50">
      <div className="w-full max-w-md">
        <div className="form-container">
          <div className="form-header">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'}}>
              <Baby className="w-8 h-8 text-white" />
            </div>
            <h1>Welcome to Health Record</h1>
            <p>Let's set up your health profile to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="baby-setup-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>Name *</span>
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Enter full name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                className="h-12"
                data-testid="baby-name-input"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="date_of_birth" className="flex items-center space-x-2">
                <CalendarIcon className="w-4 h-4" />
                <span>Birthday *</span>
              </Label>
              <Input
                id="date_of_birth"
                type="text"
                placeholder="DD/MM/YYYY"
                value={formData.date_of_birth}
                onChange={handleDateInput}
                onKeyDown={handleDateKeyDown}
                className="h-12"
                data-testid="baby-dob-input"
                maxLength={10}
                required
              />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center space-x-2">
                <Baby className="w-4 h-4" />
                <span>Gender *</span>
              </Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => handleInputChange('gender', 'Male')}
                  className={`h-16 rounded-lg border-2 font-semibold transition-all ${
                    formData.gender === 'Male'
                      ? 'bg-blue-500 text-white border-blue-600 shadow-lg'
                      : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
                  }`}
                  data-testid="baby-gender-male"
                >
                  👦 Male
                </button>
                <button
                  type="button"
                  onClick={() => handleInputChange('gender', 'Female')}
                  className={`h-16 rounded-lg border-2 font-semibold transition-all ${
                    formData.gender === 'Female'
                      ? 'bg-pink-500 text-white border-pink-600 shadow-lg'
                      : 'bg-white text-pink-600 border-pink-300 hover:bg-pink-50'
                  }`}
                  data-testid="baby-gender-female"
                >
                  👧 Female
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full h-12 btn-primary text-lg font-semibold"
              disabled={loading}
              data-testid="create-profile-button"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Creating Profile...</span>
                </div>
              ) : (
                'Create Profile'
              )}
            </Button>

            <div className="text-center pt-2">
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline"
                onClick={() => navigate('/login')}
              >
                Already have an account? Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BabySetup;